"use client";

import { useEffect, useMemo } from "react";
import { CardItem, OPItem } from "@/interfaces/CardItem";
import { getGameCardLink } from "@/lib/getCardLink";
import CardTile from "@/components/Card/CardTile";
import { usePriceManager } from "@/stores/priceManager";
import { priceManager } from "@/lib/priceManager";
import {
  getGameDeckBuilderConfig,
  getGameHandler,
  getTcgplayerConfig,
} from "@/games";
import { SupportedGames } from "@/types/game";
import { getPalworldCardImage } from "@/lib/games/palworld/getCardData";
import { PalworldItem } from "@/interfaces/CardItem";

interface CardGridProps {
  cards: CardItem[];
  /** Which game's card art and prices to use. */
  game?: SupportedGames;
}

/**
 * A grid of card art for one set.
 *
 * `game` used to be implicit: this component imported One Piece's getCardImage
 * and asked the price manager for "one-piece" regardless of what it was given.
 * It now resolves both through the game registry so every game can use it. The
 * default keeps existing One Piece call sites working unchanged.
 */
export default function CardGrid({ cards, game = "one-piece" }: CardGridProps) {
  const { prices, fetchPrices } = usePriceManager();
  // Which cards are stored sideways is the game's business, not the grid's.
  const handler = useMemo(() => getGameHandler(game), [game]);
  const gamePrices = prices[game];

  // Only games sold on TCGplayer have prices to fetch.
  const hasPrices = Boolean(getTcgplayerConfig(game));

  const getCardImage = useMemo(() => {
    // Palworld's cards are not bundled, so its image url is derived from the
    // card id rather than looked up in a card map.
    if (game === "palworld") {
      return (id: string, size: string) => getPalworldCardImage(id, size);
    }

    const { getCardImage: fromRegistry } = getGameDeckBuilderConfig(game);
    return (id: string, size: string, src: string) =>
      fromRegistry(id, size as any, src as any);
  }, [game]);

  useEffect(() => {
    if (hasPrices) {
      fetchPrices(game);
    }
  }, [fetchPrices, game, hasPrices]);

  const cardPrices = useMemo(() => {
    if (!gamePrices) return {};

    const priceMap: Record<string, number | null> = {};
    cards.forEach((card) => {
      // A card loaded from tcgfan-backend carries its own product id, joined on
      // server-side. Only the games with a bundled aa_data.json can resolve one
      // from the card id here in the browser.
      const productId =
        (card as PalworldItem).tcgplayer_id ??
        priceManager.getTCGPlayerProductId(game, card.id);

      if (productId && gamePrices[productId]) {
        priceMap[card.id] = parseFloat(gamePrices[productId].l);
      } else {
        priceMap[card.id] = null;
      }
    });
    return priceMap;
  }, [cards, gamePrices, game]);

  return (
    <div className="flex flex-wrap w-full mb-6 mt-6">
      {cards.map((product) => {
        const imgUrl = getCardImage(
          product.id,
          "316x440",
          (product as OPItem).region ?? "",
        );
        const cardPrice = cardPrices[product.id] ?? null;

        // PressableCard shows this in the corner badge. One Piece calls it
        // `number`; Palworld calls it `card_number`.
        const displayNumber =
          (product as OPItem).number ??
          (product as PalworldItem).card_number ??
          product.id;

        // Games without a card detail page get no link rather than a dead one.
        const href = getGameCardLink(game, product);

        const orientation = handler.getCardOrientation?.(product) ?? "portrait";

        return (
          <div
            key={product.id}
            className="w-1/3 p-1 mb-2 flex items-center justify-center sm:w-1/6 md:w-1/6 md:mb-4"
          >
            <CardTile
              src={imgUrl}
              alt={product.name}
              orientation={orientation}
              number={displayNumber}
              price={cardPrice}
              caption={product.name}
              href={href || undefined}
              radiusClassName="rounded-sm"
              wrapperClassName="md:transition md:ease-in-out md:hover:-translate-y-2"
            />
          </div>
        );
      })}
    </div>
  );
}
